<template>
  <aside :class="['sidenav', { collapsed, open }]">
    <div class="scroll-fade top" aria-hidden="true"></div>
    <div class="scroll-fade bottom" aria-hidden="true"></div>
    <div class="sidenav-scroll">
      <div class="brand">
        <router-link class="profile-link" to="/dashboard/profile" @click="$emit('navigate')">
          <div class="avatar" :class="{ 'has-image': avatarSrc }">
            <img v-if="avatarSrc" :src="avatarSrc" alt="Avatar de perfil" />
            <span v-else>{{ initials }}</span>
          </div>
          <div class="profile-name" v-if="!collapsed">
            <span class="first-name">{{ firstName }}</span>
            <span v-if="lastName" class="last-name">{{ lastName }}</span>
          </div>
        </router-link>
      </div>
      <div v-if="!collapsed" class="brand-separator"></div>
      <nav class="menu">
        <div class="divider" v-if="!collapsed">Finanzas</div>
        <router-link class="item" to="/dashboard/overview" @click="$emit('navigate')">
          <span class="dot"></span>
          <span v-if="!collapsed">Inversiones</span>
        </router-link>
        <router-link class="item" to="/dashboard/expenses" @click="$emit('navigate')">
          <span class="dot"></span>
          <span v-if="!collapsed">Gastos</span>
        </router-link>
        <router-link class="item" to="/dashboard/external-assets" @click="$emit('navigate')">
          <span class="dot"></span>
          <span v-if="!collapsed">Otros Activos</span>
        </router-link>



        <div class="divider" v-if="!collapsed">Documentos</div>
        <router-link class="item" to="/dashboard/documents" @click="$emit('navigate')">
          <span class="dot"></span>
          <span v-if="!collapsed">Personales</span>
        </router-link>
        <router-link class="item" to="/dashboard/health" @click="$emit('navigate')">
          <span class="dot"></span>
          <span v-if="!collapsed">Salud</span>
        </router-link>
        <div class="divider" v-if="!collapsed">Tareas</div>
        <router-link class="item" to="/dashboard/tasks" @click="$emit('navigate')">
          <span class="dot"></span>
          <span v-if="!collapsed">Tablero</span>
        </router-link>
        <div class="divider" v-if="!collapsed">Gestión</div>
        <router-link class="item" to="/dashboard/maintenance/vehicles" @click="$emit('navigate')">
          <span class="dot"></span>
          <span v-if="!collapsed">Vehículos</span>
        </router-link>
        <router-link class="item" to="/dashboard/maintenance/home" @click="$emit('navigate')">
          <span class="dot"></span>
          <span v-if="!collapsed">Casa</span>
        </router-link>
        <router-link class="item" to="/dashboard/maintenance/taxes" @click="$emit('navigate')">
          <span class="dot"></span>
          <span v-if="!collapsed">Impuestos</span>
        </router-link>
        <router-link class="item" to="/dashboard/insurances" @click="$emit('navigate')">
          <span class="dot"></span>
          <span v-if="!collapsed">Seguros</span>
        </router-link>
        <div class="divider" v-if="!collapsed">Administración</div>
        <router-link class="item" to="/dashboard/admin/files" @click="$emit('navigate')">
          <span class="dot"></span>
          <span v-if="!collapsed">Explorador de archivos</span>
        </router-link>
      </nav>
      <div v-if="!collapsed" class="version">v{{ appVersion }}</div>
    </div>
  </aside>
</template>

<script>
import { fetchMe } from "../../services/profile";
import appMeta from "../../../package.json";

export default {
  name: "SideNav",
  props: {
    collapsed: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
  },
  data() {
    return {
      user: null,
    };
  },
  computed: {
    firstName() {
      const first = this.user?.firstName?.trim() || "";
      return first || "Usuario";
    },
    lastName() {
      return this.user?.lastName?.trim() || "";
    },
    initials() {
      const first = this.user?.firstName?.trim()?.[0] || "";
      const last = this.user?.lastName?.trim()?.[0] || "";
      const fallback = this.user?.email?.trim()?.[0] || "U";
      return `${first}${last}`.toUpperCase() || fallback.toUpperCase();
    },
    avatarSrc() {
      const profileImg = this.user?.profileImg ?? this.user?.profile_img ?? null;
      if (!profileImg) return "";
      if (typeof profileImg === "string") return profileImg;
      return profileImg?.data || "";
    },
    appVersion() {
      return appMeta.version || "0.0.0";
    },
  },
  async mounted() {
    await this.loadUser();
  },
  methods: {
    async loadUser() {
      try {
        const me = await fetchMe();
        this.user = {
          ...me,
          firstName: me?.firstName ?? me?.first_name ?? "",
          lastName: me?.lastName ?? me?.last_name ?? "",
          profileImg: me?.profileImg ?? me?.profile_img ?? null,
        };
      } catch (_) {
        this.user = null;
      }
    },
  },
};
</script>

<style scoped>
.sidenav {
  background: linear-gradient(180deg, rgba(12, 18, 32, 0.98), rgba(9, 14, 26, 0.98));
  border-right: 1px solid var(--border);
  padding: 0;
  width: 260px;
  min-height: 100vh;
  position: relative;
  transition: transform 0.25s ease, width 0.25s ease, padding 0.25s ease;
  z-index: 9;
}

.sidenav-scroll {
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding: 24px 18px;
}

.sidenav.collapsed {
  width: 0;
  padding: 0;
  border-right: 0;
  overflow: hidden;
  transform: translateX(-100%);
}

.sidenav.collapsed .sidenav-scroll {
  padding: 0;
}

@media (max-width: 900px) {
  .sidenav {
    position: fixed;
    inset: 0 auto 0 0;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
    width: 260px;
    box-shadow: 0 20px 50px rgba(2, 6, 23, 0.6);
    height: 100vh;
    overflow: hidden;
  }

  .sidenav-scroll {
    height: 100%;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px) + 96px);
  }

  .sidenav-scroll::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  .scroll-fade {
    position: absolute;
    left: 0;
    right: 0;
    height: 26px;
    pointer-events: none;
    z-index: 2;
  }

  .scroll-fade.top {
    top: 0;
    background: linear-gradient(
      180deg,
      rgba(12, 18, 32, 0.98),
      rgba(12, 18, 32, 0)
    );
  }

  .scroll-fade.bottom {
    bottom: 0;
    background: linear-gradient(
      0deg,
      rgba(9, 14, 26, 0.98),
      rgba(9, 14, 26, 0)
    );
  }

  .sidenav.collapsed {
    width: 260px;
    padding: 24px 18px;
    border-right: 1px solid var(--border);
    transform: translateX(-100%);
  }

  .sidenav.open {
    transform: translateX(0);
  }
}

.brand {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  justify-content: center;
}

.profile-link {
  display: flex;
  align-items: center;
  gap: 12px;
  color: inherit;
  width: 100%;
  flex-direction: column;
  text-align: center;
}

.avatar {
  width: min(96px, 70%);
  aspect-ratio: 1 / 1;
  border-radius: 999px;
  background: linear-gradient(135deg, #38bdf8, #22c55e);
  border: 1px solid rgba(56, 189, 248, 0.35);
  display: grid;
  place-items: center;
  font-weight: 700;
  color: #0b1020;
  overflow: hidden;
}

.avatar.has-image {
  background: transparent;
  border: none;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.profile-name {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.first-name {
  font-weight: 600;
  font-size: 15px;
}

.last-name {
  color: var(--muted);
  font-size: 13px;
  letter-spacing: 0.02em;
}

.brand-separator {
  height: 1px;
  width: 100%;
  background: linear-gradient(
    90deg,
    rgba(56, 189, 248, 0),
    rgba(56, 189, 248, 0.4),
    rgba(34, 197, 94, 0.4),
    rgba(34, 197, 94, 0)
  );
  margin: 10px 0 4px;
  opacity: 0.85;
}

.menu {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.version {
  margin-top: auto;
  text-align: center;
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(148, 163, 184, 0.55);
  padding-top: 8px;
}

.divider {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--muted);
  margin-top: 8px;
}

.item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  color: var(--text);
  transition: background 0.2s ease;
}

.item:hover {
  background: rgba(56, 189, 248, 0.1);
}

.item.router-link-active {
  background: rgba(56, 189, 248, 0.2);
  border: 1px solid rgba(56, 189, 248, 0.2);
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 10px rgba(56, 189, 248, 0.5);
}

.dot.small {
  width: 6px;
  height: 6px;
}

</style>
